import { useEffect, useRef, useState } from "react";
import { Plus, Save, Copy, Ticket, Check, CalendarDays, Eye, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { uploadImage } from "@/lib/upload";
import { AdventCalendar } from "@/components/AdventCalendar";

export const EditionsTab = () => {
  const [editions, setEditions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [codes, setCodes] = useState([]);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [count, setCount] = useState(10);
  const [kind, setKind] = useState("gift");
  const [shuffle, setShuffle] = useState(true);
  const [busy, setBusy] = useState(false);
  const [savedDay, setSavedDay] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingDay, setUploadingDay] = useState(null);
  const fileInputs = useRef({});

  const loadEditions = async () => {
    const { data } = await api.get("/admin/editions");
    setEditions(data);
    if (!selectedId && data.length) setSelectedId(data[0].id);
  };

  const loadEditionData = (id) => {
    api.get(`/admin/editions/${id}/rewards`).then((r) => setRewards(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get(`/admin/editions/${id}/codes`).then((r) => setCodes(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => {
    loadEditions().catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedId) loadEditionData(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const createEdition = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/editions", { name: newName, description: newDesc });
      toast.success(`Edizione "${data.name}" creata con le 24 caselle copiate dal Classico`);
      setNewName("");
      setNewDesc("");
      await loadEditions();
      setSelectedId(data.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const updateReward = (day, field, value) =>
    setRewards((rs) => rs.map((r) => (r.day === day ? { ...r, [field]: value } : r)));

  const saveReward = async (day) => {
    const reward = rewards.find((r) => r.day === day);
    try {
      await api.put(`/admin/editions/${selectedId}/rewards/${day}`, {
        title: reward.title,
        description: reward.description,
        image_url: reward.image_url || "",
        brand_name: reward.brand_name || "",
        link_url: reward.link_url || "",
      });
      setSavedDay(day);
      setTimeout(() => setSavedDay(null), 1500);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleUpload = async (day, file) => {
    if (!file) return;
    setUploadingDay(day);
    try {
      const url = await uploadImage(file);
      updateReward(day, "image_url", url);
      toast.success(`Foto caricata per la casella ${day}: ricorda di salvare`);
    } catch (err) {
      toast.error(formatApiError(err, "Caricamento non riuscito."));
    } finally {
      setUploadingDay(null);
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/editions/${selectedId}/codes`, { count, kind, shuffle });
      toast.success(
        shuffle
          ? `${data.created} codici generati, ognuno con il suo ordine regali mescolato`
          : `${data.created} codici generati con l'ordine regali dell'edizione`
      );
      loadEditionData(selectedId);
      loadEditions();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copyAll = () => {
    const active = codes.filter((c) => !c.redeemed_by && c.status === "attivo").map((c) => c.code);
    navigator.clipboard?.writeText(active.join("\n"));
    toast.success(`${active.length} codici copiati negli appunti`);
  };

  const inputCls =
    "w-full rounded-lg border border-amber-500/25 bg-[#0A1D15]/80 px-3 py-2 text-sm text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none focus:border-amber-400/70";
  const selected = editions.find((e) => e.id === selectedId);

  const previewDoors = rewards.map((r) => ({
    day: r.day,
    isLocked: false,
    isDemo: true,
    reward: { title: r.title, description: r.description, image_url: r.image_url || "", brand_name: r.brand_name || "", link_url: r.link_url || "" },
    unlockDate: null,
  }));

  return (
    <div className="space-y-8" data-testid="editions-tab">
      {/* editions row */}
      <div className="flex flex-wrap items-center gap-3">
        {editions.map((e) => (
          <button
            key={e.id}
            type="button"
            data-testid={`edition-chip-${e.id}`}
            onClick={() => setSelectedId(e.id)}
            className={`rounded-full border px-4 py-2.5 text-left text-sm transition-colors ${
              e.id === selectedId
                ? "border-amber-400/70 bg-amber-400/15 text-amber-200"
                : "border-amber-500/20 text-emerald-100/60 hover:border-amber-500/40"
            }`}
          >
            <span className="font-semibold">{e.name}</span>
            <span className="ml-2 text-xs opacity-70">
              {e.calendars} attivi · {e.codes} codici
            </span>
          </button>
        ))}
      </div>

      <form onSubmit={createEdition} className="flex flex-col gap-3 rounded-2xl border border-amber-500/25 bg-[#0C241A]/50 p-5 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-amber-300/80">Nuova edizione</label>
          <input
            data-testid="edition-name-input"
            required
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Es. Calendario Premium"
            className={inputCls}
          />
        </div>
        <div className="flex-1">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-amber-300/80">Descrizione</label>
          <input
            data-testid="edition-desc-input"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Es. Sorprese di lusso, ordine diverso dei regali"
            className={inputCls}
          />
        </div>
        <button
          type="submit"
          data-testid="edition-create-button"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 px-5 py-2.5 text-sm font-bold text-[#1A1204] disabled:opacity-60"
        >
          <Plus className="h-4 w-4" /> Crea edizione
        </button>
      </form>

      {selected && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-emerald-100/60">
            <CalendarDays className="mr-1.5 inline h-4 w-4 text-amber-300" />
            Stai modificando le 24 caselle di <strong className="text-amber-100">{selected.name}</strong>: qui metti ciò che i partner ti danno.
          </p>
          <button
            type="button"
            data-testid="edition-preview-toggle"
            onClick={() => setShowPreview((v) => !v)}
            className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${
              showPreview
                ? "border-amber-400/70 bg-amber-400/15 text-amber-200"
                : "border-amber-400/40 text-amber-200 hover:bg-amber-400/10"
            }`}
          >
            {showPreview ? <X className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPreview ? "Chiudi anteprima" : "Anteprima calendario"}
          </button>
        </div>
      )}

      {/* visual preview of the edition */}
      {showPreview && selected && (
        <div className="rounded-3xl border border-amber-400/30 bg-[#030B08]/60 p-4 sm:p-8" data-testid="edition-preview">
          <p className="mb-5 text-center text-xs font-semibold uppercase tracking-[0.25em] text-amber-300/80">
            Anteprima di {selected.name} — clicca le caselle per vedere le sorprese
          </p>
          <div className="mx-auto max-w-3xl">
            <AdventCalendar doors={previewDoors} />
          </div>
        </div>
      )}

      {/* 24 doors editor */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="rewards-grid">
        {rewards.map((r) => (
          <div key={r.day} className="rounded-2xl border border-amber-500/20 bg-[#0C241A]/60 p-4" data-testid={`reward-editor-${r.day}`}>
            <div className="mb-3 flex items-center justify-between">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-amber-400/50 bg-amber-400/10 font-display text-sm font-bold text-amber-300">
                {r.day}
              </span>
              <button
                type="button"
                data-testid={`reward-save-${r.day}`}
                onClick={() => saveReward(r.day)}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  savedDay === r.day
                    ? "border-emerald-400/60 text-emerald-300"
                    : "border-amber-400/40 text-amber-200 hover:bg-amber-400/10"
                }`}
              >
                {savedDay === r.day ? <Check className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
                {savedDay === r.day ? "Salvato" : "Salva"}
              </button>
            </div>

            {r.image_url ? (
              <div className="relative mb-2">
                <img src={r.image_url} alt="" className="h-24 w-full rounded-lg border border-amber-500/20 object-cover" />
                <button
                  type="button"
                  data-testid={`reward-image-remove-${r.day}`}
                  onClick={() => updateReward(r.day, "image_url", "")}
                  aria-label="Rimuovi foto"
                  className="absolute right-1.5 top-1.5 rounded-full bg-[#030B08]/80 p-1 text-red-300 hover:text-red-200"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid={`reward-upload-${r.day}`}
                disabled={uploadingDay === r.day}
                onClick={() => fileInputs.current[r.day]?.click()}
                className="mb-2 flex h-16 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-amber-400/30 text-xs font-semibold text-amber-200/60 transition-colors hover:border-amber-400/60 hover:text-amber-200"
              >
                <ImagePlus className="h-4 w-4" />
                {uploadingDay === r.day ? "Caricamento..." : "Aggiungi foto del premio"}
              </button>
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              ref={(el) => (fileInputs.current[r.day] = el)}
              onChange={(e) => handleUpload(r.day, e.target.files?.[0])}
              data-testid={`reward-file-${r.day}`}
            />

            <input
              data-testid={`reward-title-${r.day}`}
              value={r.title}
              onChange={(e) => updateReward(r.day, "title", e.target.value)}
              placeholder="Titolo sorpresa"
              className={`${inputCls} mb-2 font-semibold`}
            />
            <textarea
              data-testid={`reward-desc-${r.day}`}
              rows={2}
              value={r.description}
              onChange={(e) => updateReward(r.day, "description", e.target.value)}
              placeholder="Descrizione della sorpresa"
              className={`${inputCls} resize-none text-emerald-100/80`}
            />
            <input
              data-testid={`reward-brand-${r.day}`}
              value={r.brand_name || ""}
              onChange={(e) => updateReward(r.day, "brand_name", e.target.value)}
              placeholder="Nome brand / azienda (opzionale)"
              className={`${inputCls} mt-2`}
            />
            <input
              data-testid={`reward-link-${r.day}`}
              value={r.link_url || ""}
              onChange={(e) => updateReward(r.day, "link_url", e.target.value)}
              placeholder="Link riferimento, es. https://www.brand.it/offerta"
              className={`${inputCls} mt-2`}
            />
          </div>
        ))}
      </div>

      {/* bulk codes */}
      <div className="rounded-2xl border border-amber-500/25 bg-[#0C241A]/50 p-6" data-testid="bulk-codes-panel">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold text-amber-100">
          <Ticket className="h-5 w-5 text-amber-300" /> Genera codici in blocco
        </h3>
        <p className="mt-1.5 text-sm text-emerald-100/60">
          Crea un tot di calendari in una volta. Con la mescolanza attiva, ogni codice avrà i regali
          in un ordine diverso: i clienti non troveranno tutti lo stesso premio nello stesso giorno.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-amber-300/80">Quantità</label>
            <input
              type="number"
              min={1}
              max={100}
              data-testid="bulk-count-input"
              value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
              className="w-28 rounded-lg border border-amber-500/25 bg-[#0A1D15]/80 px-3 py-2.5 text-sm text-[#FAF6EE] outline-none focus:border-amber-400/70"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-amber-300/80">Tipo</label>
            <select
              data-testid="bulk-kind-select"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="rounded-lg border border-amber-500/25 bg-[#0A1D15]/80 px-3 py-2.5 text-sm text-[#FAF6EE] outline-none focus:border-amber-400/70"
            >
              <option value="gift">Regalo</option>
              <option value="omaggio">Omaggio</option>
            </select>
          </div>
          <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-amber-500/25 px-4 py-2.5 text-sm text-amber-100" data-testid="bulk-shuffle-label">
            <input
              type="checkbox"
              data-testid="bulk-shuffle-checkbox"
              checked={shuffle}
              onChange={(e) => setShuffle(e.target.checked)}
              className="h-4 w-4 accent-amber-500"
            />
            Mescola l'ordine dei regali
          </label>
          <button
            type="button"
            data-testid="bulk-generate-button"
            disabled={busy}
            onClick={generate}
            className="rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 px-5 py-2.5 text-sm font-bold text-[#1A1204] disabled:opacity-60"
          >
            Genera {count} codici
          </button>
          <button
            type="button"
            data-testid="bulk-copy-button"
            onClick={copyAll}
            className="inline-flex items-center gap-2 rounded-xl border border-amber-400/40 px-4 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-400/10"
          >
            <Copy className="h-4 w-4" /> Copia attivi
          </button>
        </div>

        <ul className="mt-5 grid max-h-64 gap-2 overflow-y-auto pr-1 sm:grid-cols-2 lg:grid-cols-3" data-testid="codes-list">
          {codes.map((c) => (
            <li
              key={c.id}
              data-testid={`code-row-${c.code}`}
              className="flex items-center justify-between gap-2 rounded-lg border border-amber-500/10 px-3.5 py-2.5 text-sm"
            >
              <span className="font-mono text-xs font-bold tracking-wider text-amber-100">{c.code}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                c.redeemed_by ? "bg-emerald-400/15 text-emerald-300" : "bg-amber-400/15 text-amber-300"
              }`}>
                {c.redeemed_by ? "Attivato" : c.kind === "omaggio" ? "Omaggio" : "Regalo"}
              </span>
            </li>
          ))}
          {codes.length === 0 && <li className="text-sm text-emerald-100/50">Nessun codice per questa edizione.</li>}
        </ul>
      </div>
    </div>
  );
};
